import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Lock } from 'lucide-react';
import { UserRank, getRankProgress, getNextRank, RANK_TIERS } from '../lib/rankCalculator';

interface RankCardProps {
  rank: UserRank;
  objectsDiscovered: number;
  isNew?: boolean;
  onPress?: () => void;
}

export default function RankCard({ rank, objectsDiscovered, isNew = false, onPress }: RankCardProps) {
  const nextRank = getNextRank(rank);
  const { pct, remaining } = getRankProgress(rank, objectsDiscovered);
  const isMax = rank.maxObjects === null;
  const tierNo = RANK_TIERS.findIndex(t => t.key === rank.key) + 1;
  const barRef = useRef<HTMLDivElement>(null);

  // Animate progress bar from 0 → pct on mount
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    bar.style.width = '0%';
    const raf = requestAnimationFrame(() => {
      bar.style.transition = 'width 900ms cubic-bezier(0.34, 1.56, 0.64, 1)';
      bar.style.width = `${pct}%`;
    });
    return () => cancelAnimationFrame(raf);
  }, [pct]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onPress?.();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 26, delay: 0.05 }}
      onClick={onPress}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Rank: ${rank.title}. ${isMax ? 'Maximum rank reached.' : `${objectsDiscovered} of ${rank.maxObjects} to ${nextRank?.title}.`} Tap for details.`}
      className="mb-4 rounded-2xl border border-primary/30 bg-white/[0.04] backdrop-blur-md cursor-pointer active:scale-[0.98] transition-transform overflow-hidden select-none focus-ring"
      style={{ boxShadow: `0 4px 24px ${rank.color}18` }}
    >
      <div className="px-4 pt-3.5 pb-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Badge with optional new-rank pulse ring */}
            <div className="relative shrink-0">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm"
                style={{ background: `${rank.color}20`, border: `1px solid ${rank.color}40` }}
              >
                {rank.emoji}
              </div>
              {isNew && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#13213b] animate-ping"
                  style={{ background: rank.color }}
                />
              )}
            </div>

            {/* Title + tagline */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold tracking-wide" style={{ color: rank.color }}>
                  {rank.title.toUpperCase()}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: rank.color }}>
                  (Tier {tierNo})
                </span>
                {isNew && (
                  <span
                    className="text-xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md"
                    style={{ background: `${rank.color}25`, color: rank.color }}
                  >
                    New!
                  </span>
                )}
              </div>
              <p className="text-xs text-white/80 mt-0.5 truncate">{rank.tagline}</p>
            </div>
          </div>

          {/* Tap hint */}
          <div className="shrink-0 flex items-center gap-1 text-slate-300 mt-1">
            <span className="text-xs font-bold uppercase tracking-wider hidden sm:block">Details</span>
            <ChevronRight size={16} />
          </div>
        </div>

        {/* Progress bar */}
        {!isMax ? (
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-slate-300 font-bold tabular-nums">
                {objectsDiscovered} / {rank.maxObjects}
              </span>
              <span className="text-xs font-bold" style={{ color: rank.color }}>
                {remaining} to {nextRank?.title}
              </span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                ref={barRef}
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${rank.color}, ${rank.color}88)` }}
              />
            </div>

            {/* Next rank ghost */}
            {nextRank && (
              <div className="flex items-center justify-between mt-2.5 pt-1.5 border-t border-white/5 text-[10px] leading-none">
                <div className="flex items-center gap-1.5">
                  <Lock size={13} className="text-slate-400 shrink-0" />
                  <span className="text-slate-300 font-bold">
                    {nextRank.title} unlocks at {nextRank.minObjects} experience{nextRank.minObjects !== 1 ? 's' : ''}
                  </span>
                </div>
                <span className="font-medium" style={{ color: `${rank.color}CC` }}>
                  Next: {nextRank.emoji} {nextRank.title}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-2 rounded-full" style={{ background: `${rank.color}40` }}>
              <div className="h-full w-full rounded-full" style={{ background: rank.color }} />
            </div>
            <span className="text-xs font-black uppercase tracking-widest" style={{ color: rank.color }}>
              Max Rank
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
